<script setup>
import pricing from "~/data/pricing-features.json";
import { foundingFirstYear, foundingSignupUrl } from "~/data/founding-offer";

const props = defineProps({
  location: { type: String, required: true },
  featured: { type: String, required: true },
});

const { addReferralToUrl } = useReferral();
const { localePath } = useWorkspaceLocale();

// Founding 100 offer (data/founding-offer.ts): the founding first year on yearly billing
// under each monthly list price (the offer is yearly only), until the client sees the deadline has passed.
const founding = useFoundingOffer();
const showFounding = computed(() => founding.enabled && !founding.expired.value);
const signupUrl = (slug) => {
  const href = `https://app.workspace369.com/signup?plan=${slug}`;
  return addReferralToUrl(founding.enabled ? foundingSignupUrl(href) : href);
};

// Every listed feature must be "yes" for that plan in the canonical matrix; anything else is dropped.
const rows = Object.fromEntries(pricing.categories.flatMap((c) => c.rows).map((r) => [r.key, r]));
const planIndex = Object.fromEntries(pricing.plans.map((p, i) => [p.slug, i]));
const highlights = {
  cadet: ["publicProposalLinks", "proposalDeposits", "estimateToInvoice", "billableTime", "intakeForms"],
  voyager: ["sms", "voice", "sharedInbox", "missedCallTextBack", "noAnswerForwarding"],
  specialist: ["onlineBooking", "expenses", "paymentPlans", "inventory"],
};
const fit = { cadet: "Proposals, invoices, time and intake", voyager: "Take calls and texts, share an inbox", specialist: "Bookings, expenses and payment plans" };
const plans = pricing.plans
  .filter((p) => p.slug in highlights)
  .map((p) => ({
    ...p,
    features: highlights[p.slug].filter((key) => rows[key]?.cells[planIndex[p.slug]] === "yes").map((key) => rows[key].label),
  }));
</script>

<template>
  <section id="solo-pricing" class="solo-offer" data-sticky-cta-hide aria-labelledby="solo-offer-title">
    <div class="mx-auto max-w-6xl px-4 py-14 sm:px-6 md:py-16">
      <div class="solo-offer-heading">
        <h2 id="solo-offer-title">Pick the plan your work needs today.</h2>
        <span>Client records, proposals, invoices, online payments, time tracking and intake forms are on every plan. Upgrade when your work needs calls, texts or more.</span>
      </div>
      <div class="solo-offer-grid">
        <article v-for="plan in plans" :key="plan.slug" :class="{ featured: plan.slug === props.featured }">
          <small>{{ fit[plan.slug] }}</small>
          <h3>{{ plan.name }}</h3>
          <p class="solo-offer-price">
            <strong>${{ plan.monthly }}</strong> / month
          </p>
          <p v-if="showFounding" class="solo-offer-founding" data-founding-offer>Founding offer: ${{ foundingFirstYear(String(plan.monthly)) }} for your first year, billed yearly</p>
          <p class="solo-offer-seats">{{ plan.seats === 1 ? "1 seat included" : `${plan.seats} seats included` }}</p>
          <p v-if="plan.slug !== 'cadet'" class="solo-offer-plus">{{ plan.slug === "voyager" ? "Everything in Cadet, plus" : "Everything in Voyager, plus" }}</p>
          <ul>
            <li v-for="feature in plan.features" :key="feature">{{ feature }}</li>
          </ul>
          <a
            :href="signupUrl(plan.slug)"
            data-conversion-event="start_free_click"
            :data-conversion-label="`Try ${plan.name}`"
            :data-conversion-location="`${props.location}-solo-${plan.slug}`"
            :data-conversion-plan="plan.slug"
          >Start {{ plan.name }} trial <span aria-hidden="true">→</span></a>
          <TrialDisclosure :plan="plan.slug" />
        </article>
      </div>
      <p class="solo-offer-note">
        <span v-if="showFounding" data-founding-offer>Founding member offer on yearly plans: your first year costs the same as 6 months, for the first 100 customers who join by Oct 31. </span>USD monthly billing.
        <NuxtLink :to="localePath('/pricing')" data-conversion-event="pricing_click" data-conversion-label="Compare all plans" :data-conversion-location="`${props.location}-solo`">Compare all plans</NuxtLink>.
      </p>
    </div>
    <SwitchSave :location="props.location" />
  </section>
</template>

<style scoped>
.solo-offer { scroll-margin-top: 120px; padding-bottom: 1rem; background: #fff; color: #0f172a; }
.dark .solo-offer { background: #020617; color: #fff; }
.solo-offer-heading { max-width: 44rem; }
.solo-offer-heading h2 { margin-top: 0; font-size: clamp(1.75rem, 3vw, 2.4rem); font-weight: 800; line-height: 1.15; }
.solo-offer-heading span { display: block; margin-top: 0.8rem; color: #475569; line-height: 1.7; }
.dark .solo-offer-heading span { color: #cbd5e1; }
.solo-offer-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 1rem; margin-top: 2rem; }
.solo-offer-grid article { border: 1px solid #e2e8f0; border-radius: 10px; padding: 1.4rem; background: #fff; display: flex; flex-direction: column; }
.dark .solo-offer-grid article { border-color: rgb(255 255 255 / 0.12); background: rgb(255 255 255 / 0.04); }
.solo-offer-grid article.featured { border-color: #0e7490; box-shadow: 0 18px 38px -28px #0e7490; }
.solo-offer-grid small { color: #64748b; font-size: 0.8rem; }
.dark .solo-offer-grid small { color: #94a3b8; }
.solo-offer-grid h3 { margin-top: 0.4rem; font-size: 1.4rem; font-weight: 800; }
.solo-offer-price { margin-top: 0.6rem; color: #64748b; }
.solo-offer-price strong { color: #0f172a; font-size: 2rem; font-weight: 800; }
.dark .solo-offer-price { color: #cbd5e1; }
.dark .solo-offer-price strong { color: #fff; }
.solo-offer-founding { margin-top: 0.15rem; color: #0e7490; font-size: 0.82rem; font-weight: 700; }
.dark .solo-offer-founding { color: #67e8f9; }
.solo-offer-seats { margin-top: 0.2rem; font-weight: 600; }
.solo-offer-plus { margin-top: 1rem; color: #64748b; font-size: 0.85rem; font-weight: 600; }
.dark .solo-offer-plus { color: #94a3b8; }
.solo-offer-grid ul { margin-top: 0.6rem; display: grid; align-content: start; gap: 0.45rem; font-size: 0.92rem; line-height: 1.45; flex: 1; }
.solo-offer-plus + ul { margin-top: 0.4rem; }
.solo-offer-grid li { position: relative; padding-inline-start: 1.2rem; color: #334155; }
.dark .solo-offer-grid li { color: #e2e8f0; }
.solo-offer-grid li::before { content: ""; position: absolute; inset-inline-start: 0.1rem; top: 0.5em; width: 0.45rem; height: 0.45rem; border-radius: 999px; background: #0e7490; }
.dark .solo-offer-grid li::before { background: #67e8f9; }
.solo-offer-grid a { margin-top: 1.2rem; display: inline-flex; justify-content: center; align-items: center; gap: 0.4rem; min-height: 2.9rem; border-radius: 0.375rem; border: 1px solid #cbd5e1; font-weight: 700; font-size: 0.92rem; }
.solo-offer-grid article.featured a { background: #0e7490; border-color: #0e7490; color: #fff; }
.solo-offer-grid a:hover { border-color: #94a3b8; }
.solo-offer-grid article.featured a:hover { background: #155e75; }
.dark .solo-offer-grid a { border-color: rgb(255 255 255 / 0.18); }
.solo-offer-note { margin-top: 1.2rem; font-size: 0.9rem; color: #64748b; line-height: 1.7; }
.dark .solo-offer-note { color: #94a3b8; }
.solo-offer-note a { text-decoration: underline; text-underline-offset: 3px; color: inherit; }
@media (max-width: 860px) { .solo-offer-grid { grid-template-columns: 1fr; } }
</style>
